import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

export type DailyGoalType = 'time' | 'pages' | 'chapters';

export interface DailyGoalSettings {
  type: DailyGoalType;
  target: number;
}

const DEFAULT_TARGETS: Record<DailyGoalType, number> = {
  time: 15,
  pages: 20,
  chapters: 1,
};

export function useDailyGoalSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<DailyGoalSettings>({
    type: 'time',
    target: 15,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    try {
      const { data } = await supabase
        .from('user_reading_preferences')
        .select('daily_goal_type, daily_goal_target')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (data) {
        setSettings({
          type: (data.daily_goal_type as DailyGoalType) || 'time',
          target: data.daily_goal_target || 15,
        });
      }
    } catch (error) {
      console.error('Error loading daily goal settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings: DailyGoalSettings) => {
    if (!user) return false;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_reading_preferences')
        .upsert(
          {
            user_id: user.id,
            daily_goal_type: newSettings.type,
            daily_goal_target: newSettings.target,
          },
          { onConflict: 'user_id' }
        );

      if (error) throw error;

      setSettings(newSettings);
      queryClient.invalidateQueries({ queryKey: ['daily-goal-progress'] });
      return true;
    } catch (error) {
      console.error('Error saving daily goal settings:', error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const getDefaultTarget = (type: DailyGoalType) => DEFAULT_TARGETS[type];

  return {
    settings,
    loading,
    saving,
    saveSettings,
    getDefaultTarget,
  };
}
